import { Stack, usePrevious } from '@chakra-ui/react';
import {
  KeyIcon,
  UserIcon,
  UserMinusIcon,
  UserPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Bleed, Card } from '@main/complykit';
import { useImpersonateUserMutation } from '@main/graphql/mutations/ImpersonateUser.admin.generated';
import {
  AuthPrincipalTypes_Enum,
  Organization_Users_Bool_Exp,
} from '@main/graphql/types.admin.generated';
import { Order_By } from '@main/graphql/types.generated';
import { useStableCallback, useUnmountOnce } from '@main/shared/browser';
import {
  actionHelper,
  BulkSelectionToolbarPortal,
  createColumnHelper,
  errorToast,
  filterModeILikeComparison,
  filterModeIsEmpty,
  filterModeNegateCondition,
  Table,
  useAlertDialog,
  useDrawer,
  useServerPagination,
  useTableFiltersQuery,
  useTableSearchQuery,
} from '@main/ui';
import { useNavigate } from '@tanstack/react-router';
import { ColumnDef, ColumnFilter, Row, SortingState } from '@tanstack/react-table';
import useDebounce from 'ahooks/es/useDebounce';
import equal from 'fast-deep-equal';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod/v4';

import { useUpdateUserPasswordAccessAction } from '../user/disable-password-action';
import { useUpdateUserStateAction } from '../user/disable-user-action';
import { getRoleTagConfig, STATUSES } from './constants';
import {
  GetOrganizationUsersQuery,
  GetOrganizationUsersQueryVariables,
  useGetOrganizationUsersQuery,
} from './get-org-users.admin.generated';

type User = GetOrganizationUsersQuery['organization_users'][number];

const tableId = 'organization-users';

export const UsersTab = ({ orgId }: { orgId: string }) => {
  const bulkSelectionToolbarPortalRef = useRef<HTMLElement>(null);
  const { columns, onRowClick } = useUsersTableColumns(
    useStableCallback(() => {
      refetchQuery();
    }),
  );
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useTableFiltersQuery({
    columns,
    searchParam: 'orgUsersTabFilter',
  });
  const [globalFilter, setGlobalFilter] = useTableSearchQuery({ searchParam: 'orgUsersTabSearch' });

  const { refetch: refetchQuery, ...tableProps } = useServerPaginatedQuery({
    columns,
    columnFilters,
    globalFilter,
    orgId,
  });

  useUnmountOnce(() =>
    navigate({
      to: '.',
      search: (old) => ({
        ...old,
        orgUsersTabFilter: undefined,
        orgUsersTabSearch: undefined,
      }),
      params: (old) => old,
      replace: true,
    }),
  );

  return (
    <Stack spacing={6}>
      <Card>
        <Bleed>
          <Table
            id={tableId}
            columns={columns}
            itemName={{
              singular: 'user',
              plural: 'users',
            }}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            globalFilter={globalFilter}
            onGlobalFilterChange={setGlobalFilter}
            {...tableProps}
            bulkSelectionToolbarPortalRef={bulkSelectionToolbarPortalRef}
            onRowClick={onRowClick}
          />
        </Bleed>
      </Card>
      <BulkSelectionToolbarPortal ref={bulkSelectionToolbarPortalRef} />
    </Stack>
  );
};

type ServerQueryInput = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<GetOrganizationUsersQuery['organization_users'][number], any>[];
  globalFilter: string;
  columnFilters: ColumnFilter[];
  sorting?: SortingState;
  orgId: string;
};
const queryConfig = {
  pageSize: 15,
  filterDebounceMs: 500,
  queryOptions: {
    refetchOnMountOrArgChange: true,
  },
};

function useServerPaginatedQuery({
  columns,
  globalFilter,
  columnFilters,
  sorting,
  orgId,
}: ServerQueryInput) {
  const [pagination, tablePaginationProps] = useServerPagination({
    tableName: 'org-users-table',
    tableId,
    columns,
    globalFilter: useDebounce(globalFilter, { wait: queryConfig.filterDebounceMs }),
    columnFilters: useDebounce(columnFilters, {
      wait: queryConfig.filterDebounceMs,
      leading: true,
    }),
    sorting,
    defaultPageSize: queryConfig.pageSize,
  });

  const variables: GetOrganizationUsersQueryVariables = {
    where: {
      ...pagination.where,
      organization_id: { _eq: orgId },
    },
    orderBy: {
      user: {
        displayName: Order_By.Asc,
      },
    },
    offset: pagination.offset,
    limit: pagination.limit,
  };
  const prevVariables = usePrevious(variables);
  const areVariablesEqual = equal(prevVariables, variables);

  const query = useGetOrganizationUsersQuery(variables, {
    ...queryConfig.queryOptions,
  });

  const [isFilterLoading, setIsFilterLoading] = useState(false);

  useLayoutEffect(() => {
    if (!areVariablesEqual) {
      setIsFilterLoading(true);
    }
  }, [areVariablesEqual]);

  useLayoutEffect(() => {
    if (!query.isFetching) {
      setIsFilterLoading(false);
    }
  }, [query.isFetching]);

  return {
    ...tablePaginationProps,
    isLoading: query.isLoading || isFilterLoading,
    data: query.data?.organization_users ?? [],
    rowCount: query.data?.organization_users_aggregate.aggregate?.count,
    refetch: query.refetch,
  };
}

const uuidSchema = z.string().uuid();
export function useUsersTableColumns(refetchQuery: () => void) {
  const { openDialog } = useAlertDialog();
  const drawer = useDrawer();

  const [impersonateUser] = useImpersonateUserMutation();
  const disablePasswordAccessAction = useUpdateUserPasswordAccessAction(refetchQuery);
  const updateUserState = useUpdateUserStateAction(refetchQuery);

  return useMemo(() => {
    const columnHelper = createColumnHelper<User, Organization_Users_Bool_Exp>();

    const openUserDrawer = (userId: string) => {
      drawer.open({ entity: 'user', entityId: userId });
    };

    return {
      onRowClick: (row: Row<User>) => openUserDrawer(row.original.user_id),
      columns: [
        columnHelper.columns.status({
          id: 'status',
          header: 'Status',
          accessorFn: ({ disabled }) => {
            const statusMeta = disabled ? STATUSES.disabled : STATUSES.active;

            return {
              value: statusMeta.value,
              tone: statusMeta.tone,
            };
          },
          size: 110,
          enableColumnFilter: true,
          getColumnFilterCondition(filterMode, filterValue) {
            return (
              filterModeIsEmpty(filterMode, (condition) => ({
                disabled: condition,
              })) ??
              filterModeNegateCondition(filterMode, {
                disabled: {
                  _in: filterValue.map((value) => (value === 'Active' ? false : true)),
                },
              })
            );
          },
        }),
        columnHelper.columns.tag({
          id: 'type',
          header: 'Type',
          accessorFn: ({ user }) => {
            const isServiceAccount = user.principalType === AuthPrincipalTypes_Enum.ServiceAccount;
            return {
              value: isServiceAccount ? 'Service Account' : 'User',
              colorScheme: isServiceAccount ? 'purple' : 'blue',
            };
          },
          size: 130,
          enableColumnFilter: true,
          enableGlobalFilter: false,
          getColumnFilterCondition(filterMode, filterValue) {
            return filterModeNegateCondition(filterMode, {
              user: {
                principalType: {
                  _in: filterValue.map((v) =>
                    v === 'Service Account'
                      ? AuthPrincipalTypes_Enum.ServiceAccount
                      : AuthPrincipalTypes_Enum.Human,
                  ),
                },
              },
            });
          },
        }),
        columnHelper.columns.text({
          id: 'name',
          header: 'Name',
          accessorFn: ({ user }) => user.displayName,
          enableGlobalFilter: true,
          enableColumnFilter: true,
          enableHiding: false,
          size: 220,
          getGlobalFilterCondition(filterValue) {
            return { user: { displayName: { _ilike: `%${filterValue}%` } } };
          },
          getColumnFilterCondition(filterMode, filterValue) {
            return {
              user: { displayName: filterModeILikeComparison(filterMode, filterValue) },
            };
          },
        }),
        columnHelper.columns.tag({
          id: 'role',
          header: 'Role',
          accessorFn: ({ role }) => getRoleTagConfig(role),
          size: 110,
          enableColumnFilter: true,
          enableGlobalFilter: true,
          getGlobalFilterCondition(filterValue) {
            return { role: { name: { _ilike: `%${filterValue}%` } } };
          },
          getColumnFilterCondition(filterMode, filterValue) {
            return (
              filterModeIsEmpty(filterMode, (condition) => ({
                role: {
                  id: condition,
                },
              })) ??
              filterModeNegateCondition(filterMode, {
                role: {
                  id: {
                    _in: filterValue,
                  },
                },
              })
            );
          },
        }),
        columnHelper.columns.text({
          id: 'userId',
          enableGlobalFilter: true,
          header: 'User ID',
          size: 320,
          accessorFn: ({ user_id }) => user_id,
          getGlobalFilterCondition(filterValue) {
            const { success } = uuidSchema.safeParse(filterValue);
            if (success) {
              return { user_id: { _eq: filterValue } };
            }
            return;
          },
        }),

        columnHelper.columns.tag({
          id: 'passwordAccess',
          header: 'Password access',
          accessorFn: ({ user }) => {
            return {
              value: user.password_disabled ? 'Disabled' : 'Enabled',
              colorScheme: user.password_disabled ? 'gray' : 'green',
            };
          },
          size: 150,
          enableColumnFilter: true,
          getColumnFilterCondition(filterMode, filterValue) {
            return (
              filterModeIsEmpty(filterMode, (condition) => ({
                user: { password_disabled: condition },
              })) ??
              filterModeNegateCondition(filterMode, {
                user: {
                  password_disabled: {
                    _in: filterValue.map((value) => (value === 'Enabled' ? false : true)),
                  },
                },
              })
            );
          },
        }),

        columnHelper.columns.actions({
          menuActions: ({ row }) => {
            const isServiceAccount =
              row.original.user.principalType === AuthPrincipalTypes_Enum.ServiceAccount;
            const actions = [
              actionHelper.menuActionItem({
                icon: row.original.disabled ? UserPlusIcon : UserMinusIcon,
                label: row.original.disabled ? 'Enable' : 'Disable',
                onClick: () =>
                  row.original.disabled
                    ? updateUserState({
                        userIds: row.original.user_id,
                        organizationIds: row.original.organization_id,
                        disabled: false,
                      })
                    : openDialog({
                        dialogHeader: 'Disable user',
                        dialogContent: 'Are you sure you want to disable this user?',
                        confirmAction: {
                          children: 'Disable',
                          onClick: () =>
                            updateUserState({
                              userIds: row.original.user_id,
                              organizationIds: row.original.organization_id,
                              disabled: true,
                            }),
                        },
                      }),
              }),
            ];

            if (!isServiceAccount) {
              actions.push(
                actionHelper.menuActionItem({
                  icon: UserIcon,
                  label: 'Impersonate',
                  onClick: async () => {
                    try {
                      const result = await impersonateUser({
                        userId: row.original.user_id,
                      }).unwrap();
                      window.open(
                        `${import.meta.env.VITE_FRONTEND_URL}/impersonate?token=${
                          result.impersonate_user?.refreshToken
                        }`,
                      );
                    } catch {
                      errorToast('unable to impersonate user');
                    }
                  },
                }),
                actionHelper.menuActionItem({
                  icon: KeyIcon,
                  label: row.original.user.password_disabled
                    ? 'Enable password access'
                    : 'Disable password access',
                  onClick: async () => {
                    openDialog({
                      dialogHeader: row.original.user.password_disabled
                        ? 'Enable password access'
                        : 'Disable password access',
                      dialogContent: `Are you sure you want to ${row.original.user.password_disabled ? 'enabled' : 'disable'} password access for this user?`,
                      confirmAction: {
                        children: row.original.user.password_disabled ? 'Enable' : 'Disable',
                        onClick: () =>
                          disablePasswordAccessAction({
                            userIds: row.original.user.id,
                            isPasswordDisabled: !row.original.user.password_disabled,
                          }),
                      },
                    });
                  },
                }),
              );
            }

            return actions;
          },
          meta: {
            bulkActions: [
              (rows) => {
                const userIds = rows.map((row) => row.original.user_id);
                const organizationIds = rows.map((row) => row.original.organization_id);
                const isUserDisabled = rows.some((row) => row.original.disabled);

                if (!isUserDisabled) {
                  return;
                }

                return actionHelper.menuActionItem({
                  icon: UserPlusIcon,
                  label: 'Enable',
                  onClick: () =>
                    updateUserState({
                      userIds,
                      organizationIds,
                      disabled: false,
                    }),
                });
              },
              (rows) => {
                const userIds = rows.map((row) => row.original.user_id);
                const organizationIds = rows.map((row) => row.original.organization_id);
                const isUserEnabled = rows.some((row) => !row.original.disabled);

                if (!isUserEnabled) {
                  return;
                }

                return actionHelper.menuActionItem({
                  icon: UserMinusIcon,
                  label: 'Disable',
                  onClick: () =>
                    openDialog({
                      dialogHeader: 'Disable users',
                      dialogContent: 'Are you sure you want to disable selected users?',
                      confirmAction: {
                        children: 'Disable',
                        onClick: () =>
                          updateUserState({
                            userIds,
                            organizationIds,
                            disabled: true,
                          }),
                      },
                    }),
                });
              },
              (rows) => {
                const humanRows = rows.filter(
                  (row) => row.original.user.principalType === AuthPrincipalTypes_Enum.Human,
                );
                const hasServiceAccounts = rows.some(
                  (row) =>
                    row.original.user.principalType === AuthPrincipalTypes_Enum.ServiceAccount,
                );
                const userIds = humanRows.map((row) => row.original.user_id);
                const isPasswordDisabled = humanRows.some(
                  (row) => row.original.user.password_disabled,
                );

                if (userIds.length === 0 || !isPasswordDisabled) {
                  return;
                }

                return actionHelper.menuActionItem({
                  icon: KeyIcon,
                  label: 'Enable password access',
                  isDisabled: hasServiceAccounts,
                  disabledTooltip:
                    'Cannot enable password access for selection that includes service accounts',
                  onClick: async () =>
                    openDialog({
                      dialogHeader: 'Enable password access',
                      dialogContent:
                        'Are you sure you want to enable password access for selected users?',
                      confirmAction: {
                        colorScheme: 'green',
                        children: 'Enable',
                        onClick: () =>
                          disablePasswordAccessAction({
                            userIds,
                            isPasswordDisabled: false,
                          }),
                      },
                    }),
                });
              },
              (rows) => {
                const humanRows = rows.filter(
                  (row) => row.original.user.principalType === AuthPrincipalTypes_Enum.Human,
                );
                const hasServiceAccounts = rows.some(
                  (row) =>
                    row.original.user.principalType === AuthPrincipalTypes_Enum.ServiceAccount,
                );
                const userIds = humanRows.map((row) => row.original.user_id);
                const isPasswordEnabled = humanRows.some(
                  (row) => !row.original.user.password_disabled,
                );

                if (userIds.length === 0 || !isPasswordEnabled) {
                  return;
                }

                return actionHelper.menuActionItem({
                  icon: XMarkIcon,
                  label: 'Disable password access',
                  isDisabled: hasServiceAccounts,
                  disabledTooltip:
                    'Cannot disable password access for selection that includes service accounts',
                  onClick: async () =>
                    openDialog({
                      dialogHeader: 'Disable password access',
                      dialogContent:
                        'Are you sure you want to disable password access for selected users?',
                      confirmAction: {
                        children: 'Disable',
                        onClick: () =>
                          disablePasswordAccessAction({
                            userIds,
                            isPasswordDisabled: true,
                          }),
                      },
                    }),
                });
              },
            ],
          },
        }),
      ],
    };
  }, [updateUserState, disablePasswordAccessAction, drawer, impersonateUser, openDialog]);
}
